/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ReactComponent as LogoIcon } from "../logo.svg";
import React, { useEffect, useState } from "react";
import { debounce } from "underscore";

import AppBar from "@material-ui/core/AppBar";
import Button from "@material-ui/core/Button";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";

import PreviewDebugger from "./tools/debug/PreviewDebugger";
import PrintfileDebugger from "./tools/debug/PrintfileDebugger";
import PrintplateDebugger from "./tools/debug/PrintplateDebugger";

import CartDebugger from "./tools/debug/CartDebugger";

import SaveEditorButton from "./tools/SaveEditorButton";
import SavePrintplateButton from "./tools/SavePrintplateButton";

import Cart from "./tools/Cart";
import Preview from "./Preview";
import Price from "./tools/Price";
import FAQDialog from "components/tools/FAQ";
import ProductSelector from "components/ProductSelector";
import Share from "app/share/Share";
import Favorites from "app/share/Favorites";

import FavoriteIcon from "@material-ui/icons/Favorite";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import ImageIcon from "@material-ui/icons/Image";
import StorefrontIcon from "@material-ui/icons/Storefront";

import theme from "../config/theme";

import { useStoreon } from "storeon/react";

import t from "../translations";

const StyledChip = styled(Chip)({
  marginRight: 10,
});

const Logo = styled(LogoIcon)`
  width: 250px;
  margin-right: 15px;
  .st0 {
    fill: #fff !important;
  }
  @media screen and (max-width: 480px) {
    display: none;
  }
`;

const CustomLogo = styled.img`
  max-height: 50px;
  @media screen and (max-width: 480px) {
    display: none;
  }
`;

const InfoText = styled(Typography)`
  color: white;
  margin-right: 10px;
  @media screen and (max-width: 480px) {
    position: absolute;
    top: 0px;
    font-size: 9px;
    z-index: 999;
    left: 10px;
  }
`;

const fetchPrice = debounce((dispatch) => {
  dispatch("price/fetch");
}, 100);

const TopToolbar = (props) => {
  const {
    dispatch,
    isAdmin,
    editorType,
    id,
    canvas,
    product,
    config,
    proboCodes,
    contentType,
    contentId,
  } = useStoreon(
    "isAdmin",
    "id",
    "canvas",
    "editorType",
    "product",
    "config",
    "proboCodes",
    "contentType",
    "contentId"
  );

  const [square, setSquare] = useState({ percent: 0, m2: 0 });

  const handleNewCanvasClick = async (collage) => {
    dispatch("canvas/new");
  };

  useEffect(() => {
    if (!canvas) return;
    canvas.on({
      "printplate:square": (square) => {
        setSquare(square);
      },
    });
  }, [canvas]);

  useEffect(
    (_) => {
      if (!canvas) return;
      canvas.on("cutzone:updated", () => fetchPrice(dispatch));
      canvas.on("canvas:loaded", () => fetchPrice(dispatch));
    },
    [canvas, dispatch]
  );

  useEffect(() => {
    fetchPrice(dispatch);
  }, [product, dispatch, proboCodes]);

  useEffect(() => {
    if (canvas) {
      dispatch("init/app");
    }
  }, [canvas, dispatch]);

  const clipProps = {
    variant: "default",
    color: "default",
    size: "default",
  };

  return (
    <AppBar
      position="relative"
      css={css`
        z-index: ${theme.zIndex.drawer + 1};
        box-shadow: none;
        min-height: 64px;
        background: #262626;
        > div {
          min-height: 64px;
        }
        @media screen and (max-width: 480px) {
          overflow: auto;
        }
      `}
    >
      <Toolbar>
        <a href="/">
          {config.logo ? <CustomLogo src={config.logo} alt="logo" /> : <Logo />}
        </a>
        {(editorType === "printfile" || editorType === "collage") && (
          <PreviewDebugger />
        )}
        {isAdmin && editorType === "printfile" && <PrintfileDebugger />}
        {isAdmin && editorType === "printplate" && <PrintplateDebugger />}
        <Typography
          component="p"
          css={css`
            flex-grow: 1;
          `}
        />

        {isAdmin && (
          <React.Fragment>
            <Typography
              component="p"
              color="secondary"
              style={{ marginRight: 10 }}
            >
              ADMIN MODE
            </Typography>
            <Typography
              component="p"
              color="secondary"
              style={{ marginRight: 10 }}
            >
              {id ? `ID:${id} ` : "New "}
              {editorType}
            </Typography>
            {id && editorType === "printplate" && (
              <Typography
                component="p"
                color="secondary"
                style={{ marginRight: 10 }}
              >
                {square.percent.toFixed(2)}% {square.m2.toFixed(2)}m2
              </Typography>
            )}
          </React.Fragment>
        )}

        {product && (
          <StyledChip
            icon={<StorefrontIcon />}
            label={product.title}
            clickable
            variant="outlined"
            onClick={() => dispatch("productSelector/open")}
            color="primary"
            size="default"
          />
        )}

        {contentType === "favorite" && (
          <StyledChip
            icon={<FavoriteIcon />}
            label={t.editingFavoriteDesign}
            {...clipProps}
          />
        )}
        {contentType === "cartItem" && (
          <StyledChip
            icon={<ShoppingCartIcon />}
            label={t.editingCartItem}
            {...clipProps}
          />
        )}
        {contentType === "orderItem" && contentId && (
          <StyledChip
            icon={<ImageIcon />}
            label={t.editingOrderItem}
            {...clipProps}
          />
        )}

        {contentType === "orderItem" && !contentId && (
          <StyledChip
            icon={<ImageIcon />}
            label={t.creatingOrderItem}
            {...clipProps}
          />
        )}

        {isAdmin && editorType === "printfile" && <SaveEditorButton />}
        {isAdmin && editorType === "printplate" && <SavePrintplateButton />}
        <Button
          css={css`
            margin-right: 10px;
            color: #222 !important;
            @media screen and (max-width: 480px) {
              min-width: 40px;
              padding: 6px;
            }
          `}
          variant="contained"
          onClick={handleNewCanvasClick}
        >
          {t.new}
        </Button>
        {!isAdmin && <CartDebugger />}
        {!isAdmin && <ProductSelector />}
        {!isAdmin && <Cart />}
        {!isAdmin && <Share />}
        {!isAdmin && <Favorites />}
        {!isAdmin && <Price />}
        {!isAdmin && <Preview />}
      </Toolbar>
    </AppBar>
  );
};

export default TopToolbar;
